import React, { memo, useRef, useLayoutEffect, useEffect, useMemo, useState } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { unsplashTracker } from '@atlassian/jira-business-theme-unsplash';
import {
	type GradientThemeName,
	themeManager,
	type ColorThemeName,
	generateMediaImageUrl,
} from '@atlassian/jira-custom-theme-constants';
import { useMediaConfiguration } from '@atlassian/jira-router-resources-business-project-details';
import { COLUMN_FIXED_WIDTH } from '../../../../../common/constants';
import type { BoardIssueOld } from '../../../../../common/types';
import { useBoardVirtualization } from '../../../../../controllers/virtualization-context-old';
// eslint-disable-next-line camelcase
import type { JIS2_JWMBoardItemCoverMediaFragment_coverMedia_JiraWorkManagementAttachmentBackground } from '../../../../../services/board-issues/__generated_apollo__/JIS2_JWMBoardItemCoverMediaFragment';

const CARD_COVER_COLOR_HEIGHT = 24;
const CARD_COVER_IMAGE_HEIGHT = 200;

interface Props {
	coverMedia: NonNullable<BoardIssueOld['issueDetails']>['coverMedia'];
}

export const CardCover = memo(({ coverMedia }: Props) => {
	if (!coverMedia) {
		return null;
	}

	if (coverMedia.__typename === 'JiraWorkManagementColorBackground') {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const theme = themeManager.getColor(coverMedia.colorValue as ColorThemeName)?.theme;

		if (!theme) {
			return null;
		}

		return (
			<CardColorCoverContainer
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				data-testid="work-management-board.ui.board.column.card.card-cover.card-cover-color"
				backgroundColor={theme.light['color.elevation.surface']}
			/>
		);
	}

	if (coverMedia.__typename === 'JiraWorkManagementGradientBackground') {
		const theme = themeManager.getGradient(
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			coverMedia.gradientValue as GradientThemeName,
		)?.theme;

		if (!theme) {
			return null;
		}

		return (
			<CardGradientCoverContainer
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				data-testid="work-management-board.ui.board.column.card.card-cover.card-cover-gradient"
				background={theme.light['elevation.surface']}
			/>
		);
	}

	if (coverMedia.__typename === 'JiraWorkManagementAttachmentBackground') {
		return <CardImageCover coverMedia={coverMedia} />;
	}

	return null;
});

const CardImageCover = ({
	coverMedia,
}: {
	// eslint-disable-next-line camelcase
	coverMedia: JIS2_JWMBoardItemCoverMediaFragment_coverMedia_JiraWorkManagementAttachmentBackground;
}) => {
	const media = useMediaConfiguration();
	const [height, setHeight] = useState(CARD_COVER_IMAGE_HEIGHT);
	const imageRef = useRef<HTMLImageElement>(null);
	const { onBoardCardResize } = useBoardVirtualization();

	const imgUrl = useMemo(() => {
		if (
			!media?.clientId ||
			!media?.externalEndpointUrl ||
			!coverMedia?.attachment?.mediaApiFileId ||
			!coverMedia?.attachment?.mediaReadToken
		) {
			return null;
		}

		return generateMediaImageUrl({
			mediaApiFileId: coverMedia.attachment.mediaApiFileId,
			mediaClientId: media.clientId,
			mediaExternalEndpointUrl: media.externalEndpointUrl,
			token: coverMedia.attachment.mediaReadToken,
			mediaEndpoint: 'image',
			// A card is really 260px wide, but we want to load the image at a higher resolution
			// and then scale it down to avoid blurry images. If you use 260px, there will be a gap
			// between the image and the card border.
			width: COLUMN_FIXED_WIDTH,
			'max-age': Number.MAX_SAFE_INTEGER,
		});
	}, [
		coverMedia?.attachment?.mediaApiFileId,
		coverMedia?.attachment?.mediaReadToken,
		media?.clientId,
		media?.externalEndpointUrl,
	]);

	/**
	 * Because we don't have the image dimensions until the image is loaded, we need to
	 * load the image and get the natural height to set the height of the image container.
	 */
	useEffect(() => {
		if (!imgUrl) {
			return;
		}

		const img = new Image();
		img.onload = () => {
			setHeight(img.naturalHeight);
		};
		img.src = imgUrl;
	}, [imgUrl, setHeight]);

	const unsplashAttachment =
		coverMedia.attachment?.__typename === 'JiraPlatformAttachment' ? coverMedia.attachment : null;

	/**
	 * Give attribution to the unsplash api for qualified images.
	 * This is needed for legal reasons to comply with their API terms of service.
	 */
	useEffect(() => {
		if (!imgUrl) {
			return;
		}

		if (
			unsplashAttachment?.sourceType === 'UNSPLASH' &&
			unsplashAttachment?.sourceIdentifier &&
			imageRef.current
		) {
			const unsubscribe = unsplashTracker.trackWhileInViewport(
				imageRef.current,
				unsplashAttachment?.sourceIdentifier,
			);

			return unsubscribe;
		}
	}, [
		coverMedia.attachment,
		imgUrl,
		unsplashAttachment?.sourceIdentifier,
		unsplashAttachment?.sourceType,
	]);

	/**
	 * In order for card positions to appear consistently correct, we need to call
	 * onBoardCardResize when the image height changes. This is because the image
	 * height is used to calculate the height of the card.
	 */
	useLayoutEffect(() => {
		if (imageRef.current && height !== imageRef.current.clientHeight) {
			onBoardCardResize();
		}
	}, [height, onBoardCardResize]);

	if (!imgUrl) {
		return null;
	}

	return (
		<MemoizedCardImageCover
			ref={imageRef}
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			data-testid="work-management-board.ui.board.column.card.card-cover.card-cover-image"
			src={imgUrl}
			height={height}
			title={coverMedia?.attachment?.fileName ?? undefined}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardImageCoverImg = styled.div<{ src: string; height: number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: (props) => `url(${props.src})`,
	backgroundColor: token('color.background.neutral', 'white'),
	backgroundSize: 'contain',
	backgroundRepeat: 'no-repeat',
	backgroundPosition: 'center',
	maxHeight: CARD_COVER_IMAGE_HEIGHT,
	// We subtract 5px to account for the border radius on the card
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => `${props.height - 5}px`,
	width: '100%',
	borderTopLeftRadius: token('border.radius', '3px'),
	borderTopRightRadius: token('border.radius', '3px'),
});

const urlWithoutToken = (url: string) => {
	const parsedUrl = new URL(url);
	parsedUrl.searchParams.delete('token');
	return parsedUrl.toString();
};

/**
 * Since the board items are refetched after any board changes, we receive a new token
 * every time. As a result, the image is refetched and causes a flicker. We don't need
 * to rerender the image in that case. The cases we need to are when the image is removed
 * or added, in which case we will retrieve a token separately and rerender.
 */
const MemoizedCardImageCover = memo(
	CardImageCoverImg,
	(prevProps, nextProps) =>
		urlWithoutToken(prevProps.src) === urlWithoutToken(nextProps.src) &&
		prevProps.height === nextProps.height,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardGradientCoverContainer = styled.div<{ background: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	background: (props) => props.background,
	height: CARD_COVER_COLOR_HEIGHT,
	width: '100%',
	borderTopLeftRadius: token('border.radius', '3px'),
	borderTopRightRadius: token('border.radius', '3px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardColorCoverContainer = styled.div<{ backgroundColor: string }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) => props.backgroundColor,
	height: CARD_COVER_COLOR_HEIGHT,
	width: '100%',
	borderTopLeftRadius: token('border.radius', '3px'),
	borderTopRightRadius: token('border.radius', '3px'),
});
